import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Light/dark theme provider (Tier 4 — public, no data handling).
 *
 * Three modes: 'system' (default — follows the OS), 'light', 'dark'. The chosen
 * mode is persisted in localStorage under 'sh-theme' and applied as a `.dark` /
 * `.light` class on <html>, which flips the CSS-variable color tokens defined in
 * index.css. A matching pre-paint script in index.html sets the initial class to
 * avoid a flash of the wrong theme on first load.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'sh-theme';

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveDark(mode: ThemeMode): boolean {
  return mode === 'system' ? prefersDark() : mode === 'dark';
}

function applyTheme(dark: boolean): void {
  const el = document.documentElement;
  el.classList.toggle('dark', dark);
  el.classList.toggle('light', !dark);
  el.style.colorScheme = dark ? 'dark' : 'light';
}

function readStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

interface ThemeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  /** Cycle System → Light → Dark → System. */
  cycle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [isDark, setIsDark] = useState<boolean>(() => resolveDark(readStoredMode()));

  // Apply (and re-apply) whenever the mode changes.
  useEffect(() => {
    const dark = resolveDark(mode);
    setIsDark(dark);
    applyTheme(dark);
  }, [mode]);

  // While in system mode, follow live OS changes.
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      setIsDark(mq.matches);
      applyTheme(mq.matches);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = (next: ThemeMode) => {
    localStorage.setItem(STORAGE_KEY, next);
    setModeState(next);
  };

  const cycle = () => setMode(mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system');

  return (
    <ThemeContext.Provider value={{ mode, isDark, setMode, cycle }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
